import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { getSocieties } from "../../../services/societyService";
import { createParkingSlot } from "../../../services/parkingService";
import { parkingSlotCreateSchema, zodErrorToFieldErrors } from "../../../schemas/parkingSlotSchema";
import styles from "./ParkingForm.module.css";

const AddParkingSlot = () => {
  const navigate = useNavigate();

  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    society_id: "",
    slot_code: "",
    slot_type: "Car",
  });

  const loadSocieties = useCallback(async () => {
    setLoadingSocieties(true);
    try {
      const res = await getSocieties();
      setSocieties(Array.isArray(res) ? res : []);
    } finally {
      setLoadingSocieties(false);
    }
  }, []);

  useEffect(() => {
    loadSocieties();
  }, [loadSocieties]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parkingSlotCreateSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors = zodErrorToFieldErrors(parsed.error);
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      await createParkingSlot(parsed.data);
      navigate("/parking-management");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Parking Management">
      <div className={styles.page}>
        <form className={styles.card} onSubmit={handleSubmit}>
          <h2 className={styles.title}>Create Parking Slot</h2>

          <div className={styles.grid}>
            <FormField label="Society / Property" required error={errors.society_id}>
              <SelectDropdown
                value={form.society_id}
                onChange={(e) => {
                  setForm((p) => ({ ...p, society_id: e.target.value }));
                  setErrors((p) => ({ ...p, society_id: "" }));
                }}
                disabled={loadingSocieties}
                placeholder="Select society..."
                options={societies.map((s) => ({
                  label: s.society_name,
                  value: String(s.society_id),
                }))}
              />
            </FormField>

            <FormField label="Slot Code" required error={errors.slot_code}>
              <TextInput
                value={form.slot_code}
                onChange={(e) => {
                  setForm((p) => ({ ...p, slot_code: e.target.value.toUpperCase() }));
                  setErrors((p) => ({ ...p, slot_code: "" }));
                }}
                placeholder="e.g. P1 / A-12 / C-101"
                error={Boolean(errors.slot_code)}
              />
            </FormField>

            <FormField label="Slot Type" hint="Used for vehicle category matching" error={errors.slot_type}>
              <SelectDropdown
                value={form.slot_type}
                onChange={(e) => {
                  setForm((p) => ({ ...p, slot_type: e.target.value }));
                  setErrors((p) => ({ ...p, slot_type: "" }));
                }}
                placeholder="Select slot type"
                options={[
                  { label: "Car (4-Wheeler)", value: "Car" },
                  { label: "Bike (2-Wheeler)", value: "Bike" },
                  { label: "EV Charging", value: "EV" },
                  { label: "Visitor Parking", value: "Visitor" },
                  { label: "Truck / Commercial", value: "Truck" },
                ]}
              />
            </FormField>
          </div>

          <div className={styles.actions}>
            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Create Slot
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddParkingSlot;

